import axiosClient from './axiosClient';

export interface RecruitmentAIRequest {
  prompt?: string;
  context?: Record<string, unknown>;
  options?: Record<string, unknown>;
}

export interface AIJobDescription {
  name: string;
  positionName: string;
  headcount: number;
  requirements: string;
  salaryRange?: { min?: number; max?: number };
  currencySymbol?: string;
  locationName?: string;
  departmentName?: string;
  jobTypeName?: string;
}

export interface AIQuestionPool {
  name: string;
  questions: string[];
}

export interface AIInterviewCompetency {
  name: string;
  description: string;
  minimumScore: number;
  maximumScore: number;
}

export interface AIInterviewTemplate {
  name: string;
  description: string;
  questionPoolNames?: string[];
  competencies: AIInterviewCompetency[];
}

export interface AICVMatch {
  summary: string;
  matchScore: number;
  strengths: string[];
  gaps: string[];
  recommendations?: string[];
  recommendedAction?: 'shortlist' | 'review' | 'reject';
  candidateProcessId?: string;
  candidateRequisitionId?: string;
}

export interface AICandidateInterviewQuestions {
  name: string;
  questions: string[];
  focusAreas?: string[];
}

export interface AIEvaluationSummary {
  summary: string;
  strengths: string[];
  concerns: string[];
  recommendation: string;
  recommendedAction?: 'advance' | 'hold' | 'reject';
  candidateProcessId?: string;
  candidateRequisitionId?: string;
}

export interface AIHiringStage {
  name: string;
  eventType: 'Interview' | 'Test' | 'Other';
  statusCandidate: string;
  testType?: string;
  description?: string;
}

export interface AIHiringWorkflow {
  name: string;
  description: string;
  stages: AIHiringStage[];
}

export interface AICandidateSearch {
  intent?: 'keyword' | 'ai';
  keyword?: string;
  confidence?: number;
  filters: Record<string, unknown>;
  explanation: string;
}

export interface AIJobSetupQuestion {
  id: string;
  question: string;
  reason: string;
  choices: string[];
}

export interface AICompleteJobSetup {
  job: {
    name: string;
    description: string;
    positionName: string;
    headcount: number;
    locationId: number;
    locationName: string;
    departmentId: string;
    departmentName: string;
    jobTypeId: number;
    jobTypeName: string;
    minimumSalary?: number;
    maximumSalary?: number;
    currencySymbol?: string;
    requirements: string;
    openDate?: string;
    closeDate?: string;
    createdReason: string;
  };
  questionPool: AIQuestionPool & { description: string };
  interviewTemplate: AIInterviewTemplate;
  workflow: AIHiringWorkflow;
}

export interface AICompleteJobSetupResponse {
  status: 'needs_clarification' | 'ready';
  assistantMessage: string;
  clarificationQuestions: AIJobSetupQuestion[];
  setup?: AICompleteJobSetup;
}

export interface AICompleteJobSetupCreated {
  job: { id: string; jobCode: string; status: string };
  jobTemplate: { id: string; name: string };
  questionPool: { id: string; name: string };
  interviewTemplate: { id: string; name: string };
  workflowTemplate: { id: string; name: string };
}

const AI_TIMEOUT = 65000;

const post = <T>(path: string, payload: RecruitmentAIRequest): Promise<T> =>
  axiosClient.post(`/recruitment/ai/${path}`, payload, { timeout: AI_TIMEOUT }) as Promise<T>;

const recruitmentAiApi = {
  generateJobDescription: (payload: RecruitmentAIRequest) =>
    post<AIJobDescription>('job-description', payload),
  generateQuestionPool: (payload: RecruitmentAIRequest) =>
    post<AIQuestionPool>('question-pool', payload),
  generateInterviewTemplate: (payload: RecruitmentAIRequest) =>
    post<AIInterviewTemplate>('interview-template', payload),
  analyzeCVMatch: (payload: RecruitmentAIRequest) => post<AICVMatch>('cv-match', payload),
  generateCandidateInterviewQuestions: (payload: RecruitmentAIRequest) =>
    post<AICandidateInterviewQuestions>('candidate-interview-questions', payload),
  summarizeEvaluation: (payload: RecruitmentAIRequest) =>
    post<AIEvaluationSummary>('evaluation-summary', payload),
  generateHiringWorkflow: (payload: RecruitmentAIRequest) =>
    post<AIHiringWorkflow>('hiring-workflow', payload),
  parseCandidateSearch: (payload: RecruitmentAIRequest) =>
    post<AICandidateSearch>('candidate-search', payload),
  planCompleteJobSetup: (payload: RecruitmentAIRequest) =>
    post<AICompleteJobSetupResponse>('job-setup', payload),
  createCompleteJobSetup: (setup: AICompleteJobSetup) =>
    axiosClient.post(
      '/recruitment/ai/job-setup/create',
      { setup },
      { timeout: AI_TIMEOUT },
    ) as Promise<AICompleteJobSetupCreated>,
};

export default recruitmentAiApi;
